<app-auth-container>
  <ng-template #content>
    <div class="col-md-11 col-12 mx-auto complete-register-container">

      <!-- ======================================================== -->
      <!-- LOADING LINK VALIDATION STATE -->
      <!-- ======================================================== -->
      <div *ngIf="isLoadingLink" class="text-center py-5">
        <div class="spinner-border text-primary mb-3" role="status" style="width: 3rem; height: 3rem;"></div>
        <p class="text-muted">{{ "Invitation.VERIFYING_LINK" | translate }}</p>
      </div>

      <!-- ======================================================== -->
      <!-- EXPIRED / CANCELLED / INVALID LINK STATE -->
      <!-- ======================================================== -->
      <div *ngIf="!isLoadingLink && linkStatus !== 'Valid'" class="status-error-container text-center py-4">
        <div class="status-icon-wrapper mb-3" [ngClass]="linkStatus?.toLowerCase()">
          <i *ngIf="linkStatus === 'Expired'" class="ri-time-line"></i>
          <i *ngIf="linkStatus === 'Cancelled'" class="ri-close-circle-line"></i>
          <i *ngIf="linkStatus !== 'Expired' && linkStatus !== 'Cancelled'" class="ri-error-warning-line"></i>
        </div>

        <h2 class="font-weight-700 mb-3">
          <ng-container *ngIf="linkStatus === 'Expired'">{{ "Invitation.EXPIRED_TITLE" | translate }}</ng-container>
          <ng-container *ngIf="linkStatus === 'Cancelled'">{{ "Invitation.CANCELLED_TITLE" | translate }}</ng-container>
          <ng-container *ngIf="linkStatus !== 'Expired' && linkStatus !== 'Cancelled'">{{ "Invitation.INVALID_TITLE" | translate }}</ng-container>
        </h2>

        <p class="status-message mb-4">{{ linkMessage }}</p>

        <!-- Support contacts card -->
        <div class="support-contacts-card p-3 mx-auto mb-4">
          <p class="support-card-title mb-2 font-weight-600">
            <i class="ri-customer-service-2-line me-1"></i>
            {{ "Invitation.CONTACT_SUPPORT" | translate }}
          </p>
          <div class="d-flex flex-wrap justify-content-center gap-3">
            <a [href]="'mailto:' + supportEmail" class="support-pill">
              <i class="ri-mail-line"></i>
              <span>{{ supportEmail }}</span>
            </a>
            <a [href]="'tel:' + supportPhone" class="support-pill">
              <i class="ri-phone-line"></i>
              <span dir="ltr">{{ supportPhone }}</span>
            </a>
          </div>
        </div>

        <button type="button" class="btn primary-btn px-5 py-3" [routerLink]="['/authentication/login']">
          {{ "Invitation.GO_TO_LOGIN" | translate }}
        </button>
      </div>

      <!-- ======================================================== -->
      <!-- VALID LINK: 3-STEP INVESTOR REGISTRATION JOURNEY -->
      <!-- ======================================================== -->
      <div *ngIf="!isLoadingLink && linkStatus === 'Valid'" class="journey-content">

        <!-- Journey Header -->
        <div class="journey-header mb-4">
          <h1 class="font-weight-700 journey-title">
            {{ "Invitation.TITLE" | translate }}
          </h1>
          <p class="text-muted journey-subtitle">
            {{ "Invitation.SUBTITLE" | translate }}
          </p>
        </div>

        <!-- Stepper Indicator -->
        <div class="stepper-bar mb-4">
          <div class="step-item" [ngClass]="{ 'active': currentStep === 1, 'completed': currentStep > 1 }">
            <div class="step-bubble">
              <i *ngIf="currentStep > 1" class="ri-check-line"></i>
              <span *ngIf="currentStep <= 1">1</span>
            </div>
            <span class="step-label">{{ "Invitation.STEP_VERIFY" | translate }}</span>
          </div>

          <div class="step-line" [ngClass]="{ 'completed': currentStep > 1 }"></div>

          <div class="step-item" [ngClass]="{ 'active': currentStep === 2, 'completed': currentStep > 2 }">
            <div class="step-bubble">
              <i *ngIf="currentStep > 2" class="ri-check-line"></i>
              <span *ngIf="currentStep <= 2">2</span>
            </div>
            <span class="step-label">{{ "Invitation.STEP_CONFIRM" | translate }}</span>
          </div>

          <div class="step-line" [ngClass]="{ 'completed': currentStep > 2 }"></div>

          <div class="step-item" [ngClass]="{ 'active': currentStep === 3 }">
            <div class="step-bubble">
              <span>3</span>
            </div>
            <span class="step-label">{{ "Invitation.STEP_PASSWORD" | translate }}</span>
          </div>
        </div>

        <!-- ====================================================== -->
        <!-- STEP 1: VERIFY IDENTITY (OTP) -->
        <!-- ====================================================== -->
        <div *ngIf="currentStep === 1" class="step-section step-otp">
          <div class="otp-header text-center mb-4">
            <div class="otp-icon-wrap mb-2">
              <i class="ri-shield-keyhole-line"></i>
            </div>
            <h3 class="font-weight-600 mb-1">{{ "Invitation.ENTER_6_DIGIT" | translate }}</h3>
            <p class="text-muted mb-0">
              {{ "Invitation.OTP_SENT_TO" | translate }}
              <strong *ngIf="maskedPhoneNumber" class="d-block mt-1 masked-phone" dir="ltr">{{ maskedPhoneNumber }}</strong>
            </p>
          </div>

          <!-- 6-digit OTP Input Boxes -->
          <div class="otp-boxes-container mb-3" dir="ltr">
            <input
              *ngFor="let digit of otpDigits; let i = index"
              type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              maxlength="1"
              class="otp-box"
              [id]="'otp-input-' + i"
              [value]="otpDigits[i]"
              (focus)="$any($event.target).select()"
              (input)="onOtpInput($event, i)"
              (keydown)="onOtpKeydown($event, i)"
              (paste)="onOtpPaste($event)"
              [disabled]="isVerifyingOtp || isWrongAttemptsExceeded || isCodeExpired"
              autocomplete="one-time-code"
            />
          </div>

          <!-- Timer & Alerts -->
          <div class="otp-meta mb-3">
            <!-- 5-minute validity timer -->
            <div *ngIf="!isCodeExpired && !isWrongAttemptsExceeded" class="d-flex align-items-center justify-content-center otp-timer-badge">
              <i class="ri-time-line me-1"></i>
              <span>{{ "Invitation.EXPIRES_IN" | translate }}:</span>
              <strong class="ms-1 timer-countdown">{{ formattedExpirationTime }}</strong>
            </div>

            <!-- Expired alert -->
            <div *ngIf="isCodeExpired && !isWrongAttemptsExceeded" class="alert alert-warning text-center py-2 mb-0">
              <i class="ri-error-warning-line me-1"></i>
              {{ "Invitation.CODE_EXPIRED" | translate }}
            </div>

            <!-- 5 wrong attempts locked alert -->
            <div *ngIf="isWrongAttemptsExceeded" class="alert alert-danger text-center py-2 mb-0">
              <i class="ri-alert-line me-1"></i>
              {{ "Invitation.MAX_ATTEMPTS_EXCEEDED" | translate }}
            </div>
          </div>

          <!-- Request New Code or Resend Link -->
          <div class="otp-resend-area text-center mb-4">
            <!-- If attempts exceeded or expired, show prominent Request New Code button -->
            <button
              *ngIf="isWrongAttemptsExceeded || isCodeExpired"
              type="button"
              class="btn primary-btn px-4 py-2"
              (click)="sendOtp()"
              [disabled]="isSendingOtp"
            >
              <span *ngIf="isSendingOtp" class="spinner-border spinner-border-sm me-2"></span>
              {{ "Invitation.REQUEST_NEW_CODE" | translate }}
            </button>

            <!-- Else show 60s cooldown resend link -->
            <div *ngIf="!isWrongAttemptsExceeded && !isCodeExpired" class="resend-cooldown-text">
              <span class="text-muted">{{ "Otp.NoT_GOT_CODE" | translate }}</span>
              <span *ngIf="resendCooldownSeconds > 0" class="cooldown-timer text-muted">
                {{ "Invitation.RESEND_IN" | translate }} ({{ resendCooldownSeconds }}s)
              </span>
              <a
                *ngIf="resendCooldownSeconds <= 0"
                href="javascript:void(0)"
                class="resend-action-link"
                (click)="sendOtp()"
                [class.disabled]="isSendingOtp"
              >
                <span *ngIf="isSendingOtp" class="spinner-border spinner-border-sm me-1"></span>
                {{ "Invitation.RESEND_CODE" | translate }}
              </a>
            </div>
          </div>

          <!-- Verify Button -->
          <button
            type="button"
            class="btn primary-btn btn-block py-3 w-100"
            (click)="verifyOtp()"
            [disabled]="!isOtpComplete || isVerifyingOtp || isCodeExpired || isWrongAttemptsExceeded"
          >
            <span *ngIf="isVerifyingOtp" class="spinner-border spinner-border-sm me-2"></span>
            {{ (isVerifyingOtp ? "Invitation.VERIFYING" : "Invitation.VERIFY_OTP") | translate }}
          </button>
        </div>

        <!-- ====================================================== -->
        <!-- STEP 2: CONFIRM DETAILS -->
        <!-- ====================================================== -->
        <div *ngIf="currentStep === 2" class="step-section step-confirm">
          <div class="step-subheading mb-3">
            <h3 class="font-weight-600 mb-1">{{ "Invitation.CONFIRM_DETAILS_TITLE" | translate }}</h3>
            <p class="text-muted">{{ "Invitation.CONFIRM_DETAILS_SUBTITLE" | translate }}</p>
          </div>

          <div *ngIf="isLoadingDetails" class="text-center py-4">
            <div class="spinner-border text-primary" role="status"></div>
          </div>

          <div *ngIf="!isLoadingDetails" class="details-form">
            <!-- 1. National ID -->
            <div class="app-form-control mb-3">
              <label class="input-label">{{ "Invitation.NATIONAL_ID" | translate }}</label>
              <div class="readonly-input-group">
                <input
                  type="text"
                  class="w-100 readonly-control"
                  [value]="displayedNationalId"
                  readonly
                  tabindex="-1"
                />
                <i class="ri-lock-line input-lock-icon"></i>
              </div>
            </div>

            <!-- 2. Email -->
            <div class="app-form-control mb-3">
              <label class="input-label">{{ "Invitation.EMAIL" | translate }}</label>
              <div class="readonly-input-group">
                <input
                  type="text"
                  class="w-100 readonly-control"
                  [value]="displayedEmail"
                  readonly
                  tabindex="-1"
                />
                <i class="ri-lock-line input-lock-icon"></i>
              </div>
            </div>

            <!-- 3. Birth Date -->
            <div class="app-form-control mb-3">
              <label class="input-label">{{ "Invitation.BIRTH_DATE" | translate }}</label>
              <div class="readonly-input-group">
                <input
                  type="text"
                  class="w-100 readonly-control"
                  [value]="displayedBirthDate"
                  readonly
                  tabindex="-1"
                />
                <i class="ri-lock-line input-lock-icon"></i>
              </div>
            </div>

            <!-- 4. Phone Number -->
            <div class="app-form-control mb-3">
              <label class="input-label">{{ "Invitation.PHONE_NUMBER" | translate }}</label>
              <div class="readonly-input-group">
                <input
                  type="text"
                  class="w-100 readonly-control phone-readonly-control"
                  [value]="displayedPhone"
                  [style.text-align]="languageService.currentLanguage === 'ar' ? 'right' : 'left'"
                  style="direction: ltr;"
                  readonly
                  tabindex="-1"
                />
                <i class="ri-lock-line input-lock-icon"></i>
              </div>
            </div>

            <!-- "Details not correct? Contact support" -->
            <div class="details-support-link-area mt-3 pt-1">
              <button
                type="button"
                class="btn-link support-trigger-link d-inline-flex align-items-center"
                (click)="showSupportInfo = !showSupportInfo"
              >
                <i class="ri-question-line me-1"></i>
                <span>{{ "Invitation.DETAILS_NOT_CORRECT" | translate }}</span>
                <i class="ri-arrow-down-s-line ms-1" [class.rotated]="showSupportInfo"></i>
              </button>

              <!-- Collapsible Support Contacts Card -->
              <div *ngIf="showSupportInfo" class="support-info-box mt-3 p-3">
                <p class="support-box-desc mb-3 text-muted">
                  {{ "Invitation.SUPPORT_ASSISTANCE_TEXT" | translate }}
                </p>
                <div class="d-flex flex-wrap gap-2">
                  <a [href]="'mailto:' + supportEmail" class="support-contact-pill">
                    <i class="ri-mail-line"></i>
                    <span>{{ supportEmail }}</span>
                  </a>
                  <a [href]="'tel:' + supportPhone" class="support-contact-pill">
                    <i class="ri-phone-line"></i>
                    <span dir="ltr">{{ supportPhone }}</span>
                  </a>
                </div>
              </div>
            </div>

            <!-- Confirm & Continue Button -->
            <button
              type="button"
              class="btn primary-btn btn-block py-3 w-100 mt-4"
              (click)="proceedToPasswordStep()"
            >
              {{ "Invitation.CONFIRM_AND_CONTINUE" | translate }}
            </button>
          </div>
        </div>

        <!-- ====================================================== -->
        <!-- STEP 3: CREATE PASSWORD & ACCEPT TERMS -->
        <!-- ====================================================== -->
        <div *ngIf="currentStep === 3" class="step-section step-password">
          <div class="step-subheading mb-3">
            <h3 class="font-weight-600 mb-1">{{ "Invitation.CREATE_PASSWORD_TITLE" | translate }}</h3>
            <p class="text-muted">{{ "Invitation.CREATE_PASSWORD_SUBTITLE" | translate }}</p>
          </div>

          <form [formGroup]="passwordForm" autocomplete="off">
            <!-- New Password -->
            <div class="app-form-control mb-2">
              <label class="input-label" for="invitationPassword">
                {{ "Invitation.NEW_PASSWORD" | translate }}
              </label>
              <div class="input-icon-group w-100">
                <input
                  id="invitationPassword"
                  [type]="hidePassword ? 'password' : 'text'"
                  formControlName="password"
                  [placeholder]="'Invitation.NEW_PASSWORD_PLACEHOLDER' | translate"
                  class="w-100"
                  autocomplete="new-password"
                />
                <button
                  type="button"
                  mat-icon-button
                  matSuffix
                  (click)="hidePassword = !hidePassword"
                  [attr.aria-label]="'Toggle password visibility'"
                >
                  <i [class]="hidePassword ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                </button>
              </div>
            </div>

            <!-- Real-time Password Rules Checklist -->
            <div class="password-guidelines p-3 mb-3">
              <ul class="mb-0 ps-0">
                <li [ngClass]="{ 'valid': passwordRules.minChars }">
                  <i [class]="passwordRules.minChars ? 'ri-checkbox-circle-fill text-success' : 'ri-circle-line text-muted'"></i>
                  <span>{{ "Invitation.RULE_MIN_CHARS" | translate }}</span>
                </li>
                <li [ngClass]="{ 'valid': passwordRules.hasCapital }">
                  <i [class]="passwordRules.hasCapital ? 'ri-checkbox-circle-fill text-success' : 'ri-circle-line text-muted'"></i>
                  <span>{{ "Invitation.RULE_CAPITAL" | translate }}</span>
                </li>
                <li [ngClass]="{ 'valid': passwordRules.hasNumber }">
                  <i [class]="passwordRules.hasNumber ? 'ri-checkbox-circle-fill text-success' : 'ri-circle-line text-muted'"></i>
                  <span>{{ "Invitation.RULE_NUMBER" | translate }}</span>
                </li>
                <li [ngClass]="{ 'valid': passwordRules.hasSpecial }">
                  <i [class]="passwordRules.hasSpecial ? 'ri-checkbox-circle-fill text-success' : 'ri-circle-line text-muted'"></i>
                  <span>{{ "Invitation.RULE_SPECIAL" | translate }}</span>
                </li>
              </ul>
            </div>

            <!-- Confirm Password -->
            <div class="app-form-control mb-3">
              <label class="input-label" for="invitationConfirmPassword">
                {{ "Invitation.CONFIRM_PASSWORD" | translate }}
              </label>
              <div class="input-icon-group w-100">
                <input
                  id="invitationConfirmPassword"
                  [type]="hideConfirmPassword ? 'password' : 'text'"
                  formControlName="confirmPassword"
                  [placeholder]="'Invitation.CONFIRM_PASSWORD_PLACEHOLDER' | translate"
                  class="w-100"
                  autocomplete="new-password"
                />
                <button
                  type="button"
                  mat-icon-button
                  matSuffix
                  (click)="hideConfirmPassword = !hideConfirmPassword"
                  [attr.aria-label]="'Toggle password visibility'"
                >
                  <i [class]="hideConfirmPassword ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                </button>
              </div>
              <mat-error *ngIf="passwordForm.errors?.['passwordNotMatch'] && passwordForm.get('confirmPassword')?.touched">
                {{ "Login.PASSWORD_NOT_MATCH" | translate }}
              </mat-error>
            </div>

            <!-- Terms & Conditions Checkbox -->
            <div class="terms-area mt-4 mb-4 p-3 rounded">
              <div class="d-flex align-items-center gap-2">
                <mat-checkbox
                  [(ngModel)]="acceptTerms"
                  [ngModelOptions]="{ standalone: true }"
                  id="acceptTermsCheckbox"
                  color="primary"
                ></mat-checkbox>
                <label for="acceptTermsCheckbox" class="mb-0 terms-checkbox-label">
                  {{ "Invitation.ACCEPT_TERMS_PREFIX" | translate }}
                  <a href="javascript:void(0)" class="terms-dialog-link" (click)="openTermsModal()">
                    {{ "Invitation.TERMS_AND_CONDITIONS" | translate }}
                  </a>
                </label>
              </div>
            </div>

            <!-- Actions Row -->
            <div class="d-flex gap-2 mt-4">
              <button
                type="button"
                class="btn secondary-btn px-4 py-3"
                (click)="currentStep = 2"
                [disabled]="isActivating"
              >
                {{ "Shared.BACK" | translate }}
              </button>
              <button
                type="button"
                class="btn primary-btn flex-grow-1 py-3"
                (click)="activateAccount()"
                [disabled]="!acceptTerms || passwordForm.invalid || isActivating"
              >
                <span *ngIf="isActivating" class="spinner-border spinner-border-sm me-2"></span>
                {{ (isActivating ? "Invitation.ACTIVATING" : "Invitation.ACTIVATE_ACCOUNT") | translate }}
              </button>
            </div>
          </form>
        </div>

      </div>

    </div>
  </ng-template>
</app-auth-container>

<!-- ========================================================== -->
<!-- TERMS AND CONDITIONS MODAL TEMPLATE -->
<!-- ========================================================== -->
<ng-template #termsDialogRef>
  <div class="terms-modal-wrapper p-4">
    <div class="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
      <h3 class="mb-0 font-weight-700">
        {{ "Invitation.TERMS_AND_CONDITIONS" | translate }}
      </h3>
      <button type="button" class="btn-close" (click)="closeTermsModal()" aria-label="Close"></button>
    </div>

    <div class="terms-modal-body py-2" style="max-height: 55vh; overflow-y: auto;">
      <div *ngIf="isLoadingTerms" class="text-center py-5">
        <div class="spinner-border text-primary" role="status"></div>
      </div>

      <div *ngIf="!isLoadingTerms && termsData">
        <h4 class="font-weight-600 mb-2">
          {{ languageService.currentLanguage === 'ar' ? termsData.titleArabic : termsData.titleEnglish }}
        </h4>
        <div [innerHTML]="languageService.currentLanguage === 'ar' ? termsData.detailsAr : termsData.detailsEn"></div>
      </div>

      <div *ngIf="!isLoadingTerms && !termsData" class="text-muted text-center py-4">
        {{ "Invitation.TERMS_AND_CONDITIONS" | translate }}
      </div>
    </div>

    <div class="d-flex justify-content-end gap-2 pt-3 border-top mt-3">
      <button type="button" class="btn secondary-btn px-4" (click)="closeTermsModal()">
        {{ "Shared.CLOSE" | translate }}
      </button>
      <button type="button" class="btn primary-btn px-4" (click)="agreeToTermsFromModal()">
        {{ "Shared.AGREE" | translate }}
      </button>
    </div>
  </div>
</ng-template>
